/**
 * Turning API failures into something a user can act on.
 *
 * Most failure paths used to end at `console.error`, which is invisible — a
 * delete or a status change that failed looked exactly like a button that did
 * nothing. Pair these with `useToast().error(...)` in every catch block.
 */

/** Pull the most specific message an axios/FastAPI error carries. */
export function errorMessage(err: unknown, fallback: string): string {
  const anyErr = err as any;
  const detail = anyErr?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) return detail;

  // FastAPI validation errors arrive as a list of {loc, msg, type}.
  if (Array.isArray(detail)) {
    const messages = detail.map((d) => d?.msg).filter(Boolean);
    if (messages.length) return messages.join('. ');
  }

  // Redux Toolkit rejectWithValue / thrown Error.
  if (typeof anyErr === 'string' && anyErr.trim()) return anyErr;
  if (typeof anyErr?.message === 'string' && anyErr.message.trim()) {
    // Axios' own "Request failed with status code 500" tells the user nothing.
    if (!/^Request failed with status code/.test(anyErr.message)) return anyErr.message;
  }

  return fallback;
}

/**
 * Same, for requests made with `responseType: 'blob'`.
 *
 * A blob response type applies to error bodies too, so the JSON detail arrives as
 * a Blob and `err.response.data.detail` is undefined — which is why several
 * download handlers reported a generic message for errors that had a specific one.
 */
export async function parseBlobError(err: unknown, fallback: string): Promise<string> {
  const data = (err as any)?.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      if (typeof parsed?.detail === 'string' && parsed.detail.trim()) return parsed.detail;
      if (text.trim()) return text;
    } catch {
      /* Not JSON — fall through to the generic handling below. */
    }
  }

  return errorMessage(err, fallback);
}
